import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Upload as UploadIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { guestBlogOrderService } from '../../services/guestBlogOrderService';
import RichTextEditor from '../common/RichTextEditor';
import { isHtmlEmpty } from '../../utils/htmlSanitizer';
import {
  isAllowedContentDocumentFile,
  CONTENT_DOCUMENT_REJECT_MESSAGE,
} from '../../utils/contentDocumentUpload';
import { formatRoundedPrice } from '../../utils/priceRounding';
import DocumentPreviewModal from '../common/DocumentPreviewModal';

interface CartItemFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { contentText: string; contentDocUrl?: string; contentUrl?: string; orderDate?: string; submissionType: 'GBS' | 'LI' | 'BOTH'; price: number }) => void;
  siteUrl: string;
  clientName: string;
  adjustedPrice: number;
  liPrice?: number; // LI Price if available
}

const CartItemForm: React.FC<CartItemFormProps> = ({
  open,
  onClose,
  onSubmit,
  siteUrl,
  clientName,
  adjustedPrice,
  liPrice,
}) => {
  const [contentText, setContentText] = useState('');
  const [contentUrl, setContentUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedDocUrl, setUploadedDocUrl] = useState<string>('');
  const [orderDate, setOrderDate] = useState<Date | null>(new Date());
  const [submissionType, setSubmissionType] = useState<'GBS' | 'LI' | 'BOTH'>('GBS');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // Calculate price based on submission type
  const calculatePrice = (type: 'GBS' | 'LI' | 'BOTH'): number => {
    switch (type) {
      case 'GBS':
        return adjustedPrice;
      case 'LI':
        return liPrice || 0;
      case 'BOTH':
        return adjustedPrice + (liPrice || 0);
      default:
        return adjustedPrice;
    }
  };

  const currentPrice = calculatePrice(submissionType);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!isAllowedContentDocumentFile(file)) {
        setError(CONTENT_DOCUMENT_REJECT_MESSAGE);
        return;
      }
      
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      setSelectedFile(file);
      setUploadedDocUrl(''); // Reset uploaded URL when new file is selected
      setUploadSuccess(false); // Reset upload success state
      setError('');

      // Upload file immediately
      setUploading(true);
      try {
        const uploadResult = await guestBlogOrderService.uploadContentDocument(file);
        const contentDocUrl = uploadResult.data.url;
        setUploadedDocUrl(contentDocUrl); // Store for preview
        setUploadSuccess(true); // Mark upload as successful
      } catch (uploadError) {
        console.error('File upload failed:', uploadError);
        setError('Failed to upload file. Please try again.');
        setSelectedFile(null); // Clear file selection on error
      } finally {
        setUploading(false);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      if (isHtmlEmpty(contentText) && !uploadedDocUrl && !contentUrl) {
        setError('Please provide content text, upload a document, or provide a content URL');
        return;
      }

      // Count how many content options are provided
      const contentOptionsCount = [
        !isHtmlEmpty(contentText),
        !!uploadedDocUrl,
        !!contentUrl
      ].filter(Boolean).length;

      if (contentOptionsCount > 1) {
        setError('Please provide only one content option: text, document upload, or URL');
        return;
      }

      // Format date as YYYY-MM-DD
      const formattedDate = orderDate ? orderDate.toISOString().split('T')[0] : undefined;
      
      onSubmit({
        contentText,
        contentDocUrl: uploadedDocUrl,
        contentUrl: contentUrl || undefined,
        orderDate: formattedDate,
        submissionType,
        price: currentPrice
      });
      
      // Reset form
      setContentText('');
      setContentUrl('');
      setSelectedFile(null);
      setUploadedDocUrl('');
      setUploadSuccess(false);
      setError('');
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('Failed to process request. Please try again.');
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setContentText('');
      setContentUrl('');
      setSelectedFile(null);
      setUploadedDocUrl('');
      setUploadSuccess(false);
      setError('');
      setPreviewModalOpen(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Add to Cart - Content Details
      </DialogTitle>
      
      <DialogContent>
        {uploading && <LinearProgress sx={{ mb: 2 }} />}
        
        {/* Site Information */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Site Details
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            {siteUrl}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Client: {clientName}
          </Typography>
        </Box>

        {/* Submission Type Selection */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Submission Type</InputLabel>
            <Select
              value={submissionType}
              label="Submission Type"
              onChange={(e) => setSubmissionType(e.target.value as 'GBS' | 'LI' | 'BOTH')}
            >
              <MenuItem value="GBS">GB (Guest Blog) Submission</MenuItem>
              {liPrice && liPrice > 0 && (
                <MenuItem value="LI">LI (Link Insertion) Submission</MenuItem>
              )}
              {liPrice && liPrice > 0 && (
                <MenuItem value="BOTH">Both GB and LI</MenuItem>
              )}
            </Select>
          </FormControl>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Selected Price:
            </Typography>
            <Typography variant="h6" color="primary" fontWeight="bold">
              {formatRoundedPrice(currentPrice)}
            </Typography>
            {submissionType === 'BOTH' && (
              <Typography variant="caption" color="text.secondary">
                GB: {formatRoundedPrice(adjustedPrice)} + LI: {formatRoundedPrice(liPrice || 0)}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Order Month Picker */}
        <Box sx={{ mb: 3, mt: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Order Month"
              value={orderDate}
              onChange={setOrderDate}
              views={['month', 'year']}
              openTo="month"
              format="MMMM yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                  helperText: "Select month and year (e.g., November 2024)"
                }
              }}
            />
          </LocalizationProvider>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {uploadSuccess && uploadedDocUrl && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Document uploaded successfully! You can now view it using the "View Document" button below.
          </Alert>
        )}

        {/* Content Field - Rich Text Editor */}
        <Box sx={{ mb: 3 }}>
          <RichTextEditor
            value={contentText}
            onChange={setContentText}
            label={(selectedFile || contentUrl) ? "Content Requirements (Optional)" : "Content Requirements"}
            placeholder="Enter content requirements, guidelines, keywords, or any specific instructions for this guest blog post..."
            required={!selectedFile && !contentUrl}
            disabled={uploading || !!contentUrl}
            error={!!error && isHtmlEmpty(contentText) && !selectedFile && !contentUrl}
            helperText={
              (selectedFile || contentUrl)
                ? "Content is optional since you've provided a document or URL. You can still add additional requirements here if needed."
                : "Use the toolbar to format your content requirements. You can add links, lists, bold text, images (auto-compressed), and more. Alternatively, upload a document or provide a URL below."
            }
          />
        </Box>

        {/* Content URL Field */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Content Document URL (Optional)"
            placeholder="https://docs.google.com/document/d/... or any other document link"
            value={contentUrl}
            onChange={(e) => setContentUrl(e.target.value)}
            disabled={uploading || !isHtmlEmpty(contentText) || !!selectedFile}
            helperText={
              (!isHtmlEmpty(contentText) || selectedFile)
                ? "URL is optional since you've provided content text or uploaded a document."
                : "Provide a link to Google Docs, Dropbox, or any other document sharing service. This is an alternative to entering text or uploading a file."
            }
            type="url"
          />
        </Box>

        {/* File Upload */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {(isHtmlEmpty(contentText) && !contentUrl) ? "Content Document" : "Content Document (Optional)"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {(isHtmlEmpty(contentText) && !contentUrl)
              ? "Upload content guidelines, brief, or reference documents (PDF, DOC, DOCX, TXT, RTF — max 10MB). From Google Docs use File → Download → Microsoft Word (.docx). Required if no content or URL is provided above."
              : "Upload additional documents (PDF, DOC, DOCX, TXT, RTF — max 10MB). Google Docs: File → Download → Microsoft Word (.docx)."
            }
          </Typography>
          
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              disabled={uploading || !isHtmlEmpty(contentText) || !!contentUrl}
            >
              Choose File
              <input
                type="file"
                hidden
                accept=".pdf,.doc,.docx,.txt,.rtf"
                onChange={handleFileChange}
              />
            </Button>
            
            {selectedFile && (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="text.secondary">
                  {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </Typography>
                {uploadedDocUrl && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ViewIcon />}
                    onClick={() => setPreviewModalOpen(true)}
                    disabled={uploading}
                  >
                    View Document
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={uploading || (isHtmlEmpty(contentText) && !selectedFile && !contentUrl)}
        >
          {uploading ? 'Uploading...' : 'Add to Cart'}
        </Button>
      </DialogActions>

      {/* Document Preview Modal */}
      {uploadedDocUrl && (
        <DocumentPreviewModal
          open={previewModalOpen}
          onClose={() => setPreviewModalOpen(false)}
          documentUrl={uploadedDocUrl}
          fileName={guestBlogOrderService.getFileName(uploadedDocUrl)}
        />
      )}
    </Dialog>
  );
};

export default CartItemForm;
